import React, { useState, useEffect } from 'react';
import { FiCheckCircle, FiXCircle, FiEye, FiSearch, FiFileText } from 'react-icons/fi';
import api from '../../utils/api';

const font = "'Outfit', sans-serif";
const BL = '#6366f1';
const C = { text: '#1e1b4b', sub: '#6b7280', border: '#e0e7ff', bg: '#eef2ff', white: '#fff' };

const ReviewPapersHallManager = () => {
    const [papers, setPapers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [reviewingId, setReviewingId] = useState(null);
    const [reviewComment, setReviewComment] = useState('');

    const fetchPapers = async () => {
        try {
            const res = await api.get('/api/hall-manager/papers');
            setPapers(res.data.papers);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchPapers();
    }, []);

    const submitReview = async (status) => {
        if (status === 'rejected' && (!reviewComment || reviewComment.trim() === '')) {
            alert('A comment is required when rejecting a paper.');
            return;
        }
        try {
            await api.put(`/api/hall-manager/papers/${reviewingId}/review`, { status, review_comment: reviewComment });
            setReviewingId(null);
            setReviewComment('');
            fetchPapers();
        } catch (err) {
            alert(err.response?.data?.message || 'Failed to submit review');
        }
    };

    const StatusBadge = ({ s }) => {
        const styles = {
            submitted: { bg: '#fffbeb', c: '#d97706', label: 'Pending Review' },
            approved: { bg: '#ecfdf5', c: '#059669', label: 'Approved' },
            rejected: { bg: '#fef2f2', c: '#dc2626', label: 'Rejected' }
        };
        const conf = styles[s] || { bg: '#f3f4f6', c: '#4b5563', label: s };
        return (
            <span style={{ background: conf.bg, color: conf.c, padding: '4px 10px', borderRadius: 20, fontSize: 11, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                {conf.label}
            </span>
        );
    };

    const filtered = papers.filter(p =>
        p.subject_name?.toLowerCase().includes(search.toLowerCase()) ||
        p.subject_code?.toLowerCase().includes(search.toLowerCase()) ||
        p.batch_name?.toLowerCase().includes(search.toLowerCase())
    );

    return (
        <div style={{ fontFamily: font, height: '100%', display: 'flex', flexDirection: 'column' }}>
            <h1 style={{ fontSize: 24, fontWeight: 900, color: C.text, margin: '0 0 8px', letterSpacing: -0.5 }}>Review Exam Papers</h1>
            <p style={{ color: C.sub, fontSize: 14, margin: '0 0 30px' }}>Approve or reject question papers submitted by teachers before final printing and hall dispatch.</p>

            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24, background: C.white, padding: '10px 16px', borderRadius: 14, border: `1.5px solid ${C.border}`, width: '100%', maxWidth: 400, boxShadow: '0 2px 8px rgba(99,102,241,0.05)' }}>
                <FiSearch size={18} color={C.sub} />
                <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search subjects, codes, or batches..." style={{ border: 'none', background: 'transparent', outline: 'none', width: '100%', fontSize: 14, fontFamily: font, color: C.text }} />
            </div>

            <div style={{ background: C.white, borderRadius: 20, border: `1.5px solid ${C.border}`, overflow: 'hidden', boxShadow: '0 4px 12px rgba(99,102,241,0.05)', flex: 1, display: 'flex', flexDirection: 'column' }}>
                <div style={{ padding: '16px 20px', background: C.bg, borderBottom: `1px solid ${C.border}`, display: 'grid', gridTemplateColumns: 'minmax(200px, 1.5fr) 1fr 1fr 1fr 120px', gap: 16, fontSize: 12, fontWeight: 800, color: C.text, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    <div>Paper Details</div>
                    <div>Batch & Sem</div>
                    <div>Teacher</div>
                    <div>Status</div>
                    <div style={{ textAlign: 'center' }}>Actions</div>
                </div>

                <div style={{ flex: 1, overflowY: 'auto', padding: '10px 0' }}>
                    {loading ? (
                        <div style={{ padding: 40, textAlign: 'center', color: C.sub, fontSize: 13 }}>Loading submitted papers...</div>
                    ) : filtered.length === 0 ? (
                        <div style={{ padding: 40, textAlign: 'center', color: C.sub, fontSize: 13, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
                            <FiFileText size={32} color="#cbd5e1" />
                            <div>No papers pending review at the moment.</div>
                        </div>
                    ) : (
                        filtered.map(p => (
                            <div key={p.id} style={{ padding: '16px 20px', borderBottom: `1px solid ${C.border}`, display: 'grid', gridTemplateColumns: 'minmax(200px, 1.5fr) 1fr 1fr 1fr 120px', gap: 16, alignItems: 'center', transition: 'background 0.2s', ':hover': { background: '#f8fafc' } }}>
                                <div>
                                    <div style={{ fontSize: 14, fontWeight: 800, color: C.text }}>{p.subject_name}</div>
                                    <div style={{ fontSize: 12, color: C.sub, marginTop: 4 }}>{p.subject_code} • {p.title}</div>
                                </div>
                                <div>
                                    <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>{p.batch_name}</div>
                                    <div style={{ fontSize: 12, color: C.sub, marginTop: 4 }}>Sem {p.semester} ({p.department})</div>
                                </div>
                                <div style={{ fontSize: 13, fontWeight: 600, color: C.text }}>
                                    {p.teacher_username}
                                </div>
                                <div>
                                    <StatusBadge s={p.status} />
                                </div>
                                <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
                                    <a href={`/hall-manager/papers/${p.id}`} target="_blank" rel="noreferrer" style={{ width: 32, height: 32, borderRadius: 8, background: C.bg, color: BL, display: 'flex', alignItems: 'center', justifyContent: 'center', textDecoration: 'none', transition: 'all 0.2s' }}>
                                        <FiEye size={15} />
                                    </a>
                                    {p.status === 'submitted' && (
                                        <button onClick={() => setReviewingId(p.id)} style={{ padding: '0 12px', height: 32, borderRadius: 8, border: 'none', background: 'linear-gradient(135deg,#818cf8,#6366f1)', color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: font }}>
                                            Review
                                        </button>
                                    )}
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </div>

            {/* Review Modal */}
            {reviewingId && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.6)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
                    <div style={{ background: '#fff', width: '100%', maxWidth: 450, borderRadius: 24, padding: 32, boxShadow: '0 20px 40px rgba(0,0,0,0.2)' }}>
                        <h2 style={{ margin: '0 0 16px', fontSize: 20, fontWeight: 900, color: C.text }}>Provide Review</h2>
                        <p style={{ margin: '0 0 24px', fontSize: 14, color: C.sub, lineHeight: 1.5 }}>
                            You are reviewing Paper ID: {reviewingId}. Once approved, it is locked for printing. If rejected, the teacher must edit and resubmit.
                        </p>

                        <div style={{ marginBottom: 24 }}>
                            <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: BL, textTransform: 'uppercase', letterSpacing: 0.7, marginBottom: 8 }}>Review Comments (Required for Rejection) *</label>
                            <textarea value={reviewComment} onChange={e => setReviewComment(e.target.value)} rows={4} style={{ width: '100%', padding: '12px 16px', borderRadius: 12, border: `1.5px solid ${C.border}`, fontSize: 14, fontFamily: font, outline: 'none', boxSizing: 'border-box', resize: 'vertical' }} placeholder="Detail any issues requiring correction here..." />
                        </div>

                        <div style={{ display: 'flex', gap: 12 }}>
                            <button onClick={() => { setReviewingId(null); setReviewComment(''); }} style={{ flex: 1, padding: 12, borderRadius: 12, border: `1.5px solid ${C.border}`, background: C.bg, color: C.text, fontWeight: 700, fontSize: 14, cursor: 'pointer', fontFamily: font }}>
                                Cancel
                            </button>
                            <button onClick={() => submitReview('rejected')} style={{ flex: 1, padding: 12, borderRadius: 12, border: 'none', background: '#fef2f2', color: '#ef4444', fontWeight: 700, fontSize: 14, cursor: 'pointer', fontFamily: font, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                                <FiXCircle size={16} /> Reject Paper
                            </button>
                            <button onClick={() => submitReview('approved')} style={{ flex: 1, padding: 12, borderRadius: 12, border: 'none', background: '#ecfdf5', color: '#10b981', fontWeight: 700, fontSize: 14, cursor: 'pointer', fontFamily: font, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                                <FiCheckCircle size={16} /> Approve Paper
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ReviewPapersHallManager;
